import type { SupportedStorage } from '@supabase/supabase-js';
import { isNativeApp } from './platform';

const KEY_PREFIX = 'ofuesi.auth.';

const getBrowserStorage = (): SupportedStorage => {
  if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;

  const values = new Map<string, string>();
  return {
    getItem: async (key) => values.get(key) ?? null,
    setItem: async (key, value) => { values.set(key, value); },
    removeItem: async (key) => { values.delete(key); },
  };
};

const browserStorage = getBrowserStorage();
let secureStorageSetup: Promise<typeof import('@aparajita/capacitor-secure-storage')['SecureStorage']> | null = null;

const getSecureStorage = async () => {
  if (!secureStorageSetup) {
    secureStorageSetup = import('@aparajita/capacitor-secure-storage').then(async ({ SecureStorage }) => {
      await SecureStorage.setKeyPrefix(KEY_PREFIX);
      await SecureStorage.setSynchronize(false);
      return SecureStorage;
    });
  }
  return secureStorageSetup;
};

/**
 * Supabase-compatible auth storage.
 *
 * Web sessions keep using localStorage exactly as before. Native sessions are
 * kept in the iOS Keychain / Android Keystore-backed encrypted store. The
 * read path migrates any session saved by an earlier native build and removes
 * the unencrypted copy after the secure write succeeds.
 */
export const nativeAuthStorage: SupportedStorage = {
  getItem: async (key) => {
    if (!isNativeApp()) return browserStorage.getItem(key);

    const secureStorage = await getSecureStorage();
    const secureValue = await secureStorage.getItem(key);
    if (secureValue !== null) return secureValue;

    const legacyValue = await browserStorage.getItem(key);
    if (legacyValue !== null) {
      await secureStorage.setItem(key, legacyValue);
      await browserStorage.removeItem(key);
    }
    return legacyValue;
  },
  setItem: async (key, value) => {
    if (!isNativeApp()) return browserStorage.setItem(key, value);
    const secureStorage = await getSecureStorage();
    await secureStorage.setItem(key, value);
    await browserStorage.removeItem(key);
  },
  removeItem: async (key) => {
    if (!isNativeApp()) return browserStorage.removeItem(key);
    const secureStorage = await getSecureStorage();
    await secureStorage.removeItem(key);
    await browserStorage.removeItem(key);
  },
};
